import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useAuth } from './lib/auth';
import { supabaseConfigured } from './lib/supabase';
import { Spinner } from './components/ui';
import { StudentShell } from './components/StudentShell';
import { AdminShell } from './components/AdminShell';
import { SignIn, SignUp, ForgotPassword, UpdatePassword } from './pages/Auth';
import { Dashboard } from './pages/Dashboard';
import { Catalog } from './pages/Catalog';
import { CourseDetail } from './pages/CourseDetail';
import { CoursePlayer } from './pages/CoursePlayer';
import { Account, Certificates, NotFound, SetupNotice, VerifyCertificate } from './pages/misc';
import { AdminHome } from './pages/admin/AdminHome';
import { AdminCourses } from './pages/admin/AdminCourses';
import { AdminCourseEditor } from './pages/admin/AdminCourseEditor';
import { AdminStudents } from './pages/admin/AdminStudents';
import { AdminStudentDetail } from './pages/admin/AdminStudentDetail';

function RequireAuth({ children }: { children: ReactNode }) {
  const { loading, user } = useAuth();
  const loc = useLocation();
  if (loading) return <Spinner />;
  if (!user) return <Navigate to={`/sign-in?next=${encodeURIComponent(loc.pathname + loc.search)}`} replace />;
  return <>{children}</>;
}

// The UI gate is for convenience only; the database refuses staff data to everyone else.
function RequireStaff({ children }: { children: ReactNode }) {
  const { loading, user, isStaff } = useAuth();
  if (loading) return <Spinner />;
  if (!user) return <Navigate to="/sign-in?next=/admin" replace />;
  if (!isStaff) return <NotFound message="This page is for BlueLine staff." />;
  return <>{children}</>;
}

/** Supabase reports failed email links as ?error_code=… or #error_code=… on whatever page it redirects to. */
export function readAuthLinkError(search: string, hash: string): string | null {
  const q = new URLSearchParams(search);
  const h = new URLSearchParams(hash.replace(/^#/, ''));
  return q.get('error_code') ?? h.get('error_code') ?? (q.get('error') || h.get('error') ? 'access_denied' : null);
}

export function App() {
  const loc = useLocation();
  if (!supabaseConfigured) return <SetupNotice />;
  const linkError = readAuthLinkError(loc.search, loc.hash);
  if (linkError && loc.pathname !== '/sign-in') return <Navigate to={`/sign-in?link_error=${encodeURIComponent(linkError)}`} replace />;
  return (
    <Routes>
      <Route path="/sign-in" element={<SignIn />} />
      <Route path="/sign-up" element={<SignUp />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/update-password" element={<UpdatePassword />} />

      <Route element={<StudentShell />}>
        <Route path="/" element={<RequireAuth><Dashboard /></RequireAuth>} />
        <Route path="/courses" element={<Catalog />} />
        <Route path="/courses/:slug" element={<CourseDetail />} />
        <Route path="/certificates" element={<RequireAuth><Certificates /></RequireAuth>} />
        <Route path="/account" element={<RequireAuth><Account /></RequireAuth>} />
        <Route path="/verify/:publicId?" element={<VerifyCertificate />} />
        <Route path="*" element={<NotFound />} />
      </Route>

      <Route path="/learn/:slug/:lessonId?" element={<RequireAuth><CoursePlayer /></RequireAuth>} />

      <Route path="/admin" element={<RequireStaff><AdminShell /></RequireStaff>}>
        <Route index element={<AdminHome />} />
        <Route path="courses" element={<AdminCourses />} />
        <Route path="courses/:courseId" element={<AdminCourseEditor />} />
        <Route path="students" element={<AdminStudents />} />
        <Route path="students/:userId" element={<AdminStudentDetail />} />
      </Route>
    </Routes>
  );
}
